import { Controller } from "@hotwired/stimulus"

const SVG_NS = "http://www.w3.org/2000/svg"

// Stacked column chart for the Kapazitätsplanung breakdown widget. One column per period,
// each stacking the planned / free / sick / vacation / blocked segments handed over as
// [{ label, sublabel, total, segments: [{ value, color, label }] }].
//
// Sibling of dashboard_chart_controller (single-series bars) and deliberately shares its
// idioms: the chart is drawn at the canvas's measured width, and a ResizeObserver redraws
// it, because on first paint inside a lazy turbo frame the grid may not be laid out yet
// (width 0) and a neighbouring widget's resize reflows this one without re-running connect().
export default class extends Controller {
  static targets = ["canvas"]
  // format "currency" labels the axis and tooltips in euros (Kosten-Auswertung); gap leaves
  // that many px of surface between stacked segments. Both default to the capacity widget's look.
  static values = {
    data: Array,
    headcount: Number,
    format: { type: String, default: "number" },
    gap: { type: Number, default: 0 },
  }

  connect() {
    this.lastWidth = 0

    if (this.hasCanvasTarget && window.ResizeObserver) {
      this.observer = new ResizeObserver(this.scheduleRender.bind(this))
      this.observer.observe(this.canvasTarget)
    } else {
      this.renderChart()
    }
  }

  disconnect() {
    this.observer?.disconnect()
    if (this.renderScheduled) {
      cancelAnimationFrame(this.renderFrame)
      this.renderScheduled = false
    }
  }

  scheduleRender() {
    if (this.renderScheduled) return
    this.renderScheduled = true
    this.renderFrame = requestAnimationFrame(() => {
      this.renderScheduled = false
      this.renderChart()
    })
  }

  renderChart() {
    const data = this.dataValue
    if (!this.hasCanvasTarget || data.length === 0) return

    const width = this.canvasTarget.clientWidth
    // Skip while the container has no layout width (would paint an invisible chart), and
    // skip redundant redraws when the width has not actually changed.
    if (width <= 0 || width === this.lastWidth) return
    this.lastWidth = width

    const height = 200
    // A euro axis label ("13.151 €") needs far more room than a head count.
    const padding = {
      top: 16,
      right: 8,
      bottom: 34,
      left: this.formatValue === "currency" ? 72 : 28,
    }
    const chartWidth = width - padding.left - padding.right
    const chartHeight = height - padding.top - padding.bottom

    // Headcount is the honest ceiling: scaling to the tallest stack alone would make a quiet
    // week look as busy as a full one — and print an axis top above the number of people the
    // tenant has, because on multi-day spans the buckets overlap (someone planned Monday and
    // free Tuesday counts in both). A column can still exceed headcount for the same reason,
    // so the tallest stack stays in the max as the fallback ceiling.
    const maxValue = Math.max(
      this.headcountValue,
      ...data.map((column) => this.stackTotal(column, 1)),
      1,
    )
    // Costs can be negative (Gutschrift, Erstattung): those segments stack downwards from the
    // zero line, so the scale reaches below it only when some column needs it.
    const minValue = Math.min(0, ...data.map((column) => this.stackTotal(column, -1)))
    const zeroY = padding.top + (maxValue / (maxValue - minValue)) * chartHeight
    const scale = { zeroY, unit: chartHeight / (maxValue - minValue) }
    const step = chartWidth / data.length
    const columnWidth = Math.min(step * 0.62, 56)

    const svg = document.createElementNS(SVG_NS, "svg")
    svg.setAttribute("width", width)
    svg.setAttribute("height", height)

    this.appendBaseline(svg, padding, chartWidth, chartHeight, { maxValue, minValue, zeroY })

    const labelInterval = Math.max(1, Math.ceil(28 / step))

    data.forEach((column, index) => {
      const x = padding.left + index * step + (step - columnWidth) / 2
      this.appendColumn(svg, column, index, { x, columnWidth, ...scale })

      if (index % labelInterval === 0) {
        this.appendLabel(svg, column.label, x + columnWidth / 2, height - 14)
        if (column.sublabel)
          this.appendLabel(svg, column.sublabel, x + columnWidth / 2, height - 3, "#9ca3af", 9)
      }
    })

    this.canvasTarget.replaceChildren(svg)
  }

  // Sum of the segments on one side of the zero line: sign 1 the positive ones, -1 the negative.
  stackTotal(column, sign) {
    return (column.segments || []).reduce((sum, segment) => {
      const value = Number(segment.value || 0)
      return value * sign > 0 ? sum + value : sum
    }, 0)
  }

  // Zero line plus a single gridline at the top of the scale, so a column can be read as a
  // share of the workforce without a full axis competing with the data for attention. A scale
  // reaching below zero gets a gridline at its bottom as well.
  appendBaseline(svg, padding, chartWidth, chartHeight, { maxValue, minValue, zeroY }) {
    const top = padding.top
    const bottom = padding.top + chartHeight
    const lines = minValue < 0 ? [top, zeroY, bottom] : [top, zeroY]

    lines.forEach((y) => {
      const line = document.createElementNS(SVG_NS, "line")
      line.setAttribute("x1", padding.left)
      line.setAttribute("x2", padding.left + chartWidth)
      line.setAttribute("y1", y)
      line.setAttribute("y2", y)
      line.setAttribute("stroke", y === zeroY ? "#e5e7eb" : "#f3f4f6")
      svg.appendChild(line)
    })

    const axisLabel = (content, y) =>
      this.appendLabel(svg, content, padding.left - 6, y + 4, "#9ca3af", 10, "end")
    axisLabel(this.displayValue(maxValue), top)
    axisLabel("0", zeroY)
    if (minValue < 0) axisLabel(this.displayValue(minValue), bottom)
  }

  appendColumn(svg, column, index, { x, columnWidth, zeroY, unit }) {
    const segments = (column.segments || []).map((segment) => ({
      ...segment,
      value: Number(segment.value || 0),
    }))

    this.appendStack(svg, column, `${index}-up`, {
      segments: segments.filter((segment) => segment.value > 0),
      direction: -1,
      x,
      columnWidth,
      zeroY,
      unit,
    })
    this.appendStack(svg, column, `${index}-down`, {
      segments: segments.filter((segment) => segment.value < 0),
      direction: 1,
      x,
      columnWidth,
      zeroY,
      unit,
    })
  }

  // One stack growing away from the zero line: direction -1 upwards, 1 downwards.
  appendStack(svg, column, key, { segments, direction, x, columnWidth, zeroY, unit }) {
    const stackHeight = segments.reduce((sum, segment) => sum + Math.abs(segment.value) * unit, 0)
    if (stackHeight <= 0) return

    const stackTop = direction < 0 ? zeroY - stackHeight : zeroY

    // Segments are plain rectangles clipped by one rounded rect covering the whole stack, so
    // the column gets soft corners without a rounded segment leaving a notch of background
    // against the one below it.
    const group = document.createElementNS(SVG_NS, "g")
    group.setAttribute("clip-path", `url(#${this.clipId(key)})`)
    svg.appendChild(this.buildClip(key, x, stackTop, columnWidth, stackHeight))

    let consumed = 0

    segments.forEach((segment) => {
      const segmentHeight = Math.abs(segment.value) * unit
      const y = direction < 0 ? zeroY - consumed - segmentHeight : zeroY + consumed
      consumed += segmentHeight

      const rect = document.createElementNS(SVG_NS, "rect")
      rect.setAttribute("x", x)
      // The gap is taken off the end of each segment facing away from zero, so neighbours
      // never touch.
      const gap = segmentHeight > this.gapValue * 2 ? this.gapValue : 0
      rect.setAttribute("y", direction < 0 ? y + gap : y)
      rect.setAttribute("width", columnWidth)
      rect.setAttribute("height", Math.max(segmentHeight - gap, 1))
      rect.setAttribute("fill", segment.color)
      group.appendChild(rect)

      // Native tooltip: no library, but hovering a segment still names it.
      const title = document.createElementNS(SVG_NS, "title")
      title.textContent = `${column.label} · ${segment.label}: ${this.displayValue(segment.value)}`
      rect.appendChild(title)
    })

    svg.appendChild(group)
  }

  buildClip(key, x, y, width, height) {
    const clip = document.createElementNS(SVG_NS, "clipPath")
    clip.setAttribute("id", this.clipId(key))

    const rect = document.createElementNS(SVG_NS, "rect")
    rect.setAttribute("x", x)
    rect.setAttribute("y", y)
    rect.setAttribute("width", width)
    rect.setAttribute("height", Math.max(height, 1))
    rect.setAttribute("rx", Math.min(4, width / 2, Math.max(height, 1) / 2))
    clip.appendChild(rect)

    return clip
  }

  // clipPath ids are document-global, so they carry a per-controller prefix: two of these
  // widgets on one dashboard would otherwise clip each other's columns.
  clipId(key) {
    this.uid ||= Math.random().toString(36).slice(2, 8)
    return `cap-col-${this.uid}-${key}`
  }

  displayValue(value) {
    if (this.formatValue !== "currency") return String(value)

    this.currencyFormat ||= new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    })
    return this.currencyFormat.format(value)
  }

  appendLabel(svg, content, x, y, fill = "#6b7280", fontSize = 11, anchor = "middle") {
    const text = document.createElementNS(SVG_NS, "text")
    text.setAttribute("x", x)
    text.setAttribute("y", y)
    text.setAttribute("text-anchor", anchor)
    text.setAttribute("fill", fill)
    text.setAttribute("font-size", fontSize)
    text.textContent = content
    svg.appendChild(text)
  }
}
